import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCruises, Cruise } from '../context/CruiseContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  Loader2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  X,
  Link2Off,
  Filter,
  ShieldAlert,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';

interface PortUsageStats {
  officialCount: number;
  customCount: number;
  totalCount: number;
}

interface PortGroup {
  id: string;
  ports: string[];
  portDetails: Record<string, PortUsageStats>;
  selectedWinner: string;
  usageCount: number;
  officialCruiseCount: number;
  customCruiseCount: number;
  cleanupActivated: boolean; // Whether this group should be included in cleanup
}

// Normalize port names for comparison (remove extra spaces, lowercase, etc.)
const normalizePortName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:]/g, '');
};

// Calculate similarity between two port names (simple Levenshtein-like approach)
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = normalizePortName(str1);
  const s2 = normalizePortName(str2);
  
  if (s1 === s2) return 1.0;
  
  // Check if one contains the other
  if (s1.includes(s2) || s2.includes(s1)) {
    const longer = Math.max(s1.length, s2.length);
    const shorter = Math.min(s1.length, s2.length);
    return shorter / longer;
  }
  
  // Simple word-based similarity
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  const commonWords = words1.filter(w => words2.includes(w));
  const totalWords = new Set([...words1, ...words2]).size;
  
  if (totalWords === 0) return 0;
  return commonWords.length / totalWords;
};

const PortCleanup = () => {
  const navigate = useNavigate();
  const { cruises, updateCruise, loading: cruisesLoading } = useCruises();
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [portGroups, setPortGroups] = useState<PortGroup[]>([]);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.7);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'custom-only'>('all');

  // Confirmation Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingMapping, setPendingMapping] = useState<Record<string, string>>({});
  const [pendingUnlinkCruisesCount, setPendingUnlinkCruisesCount] = useState(0);

  // Compute breakdown of port usage (Official vs Custom)
  const portUsageStats = useMemo(() => {
    const stats: Record<string, PortUsageStats> = {};

    const addUsage = (portName: string, isOfficial: boolean) => {
      if (!stats[portName]) {
        stats[portName] = { officialCount: 0, customCount: 0, totalCount: 0 };
      }
      if (isOfficial) {
        stats[portName].officialCount += 1;
      } else {
        stats[portName].customCount += 1;
      }
      stats[portName].totalCount += 1;
    };

    cruises.forEach(cruise => {
      // If scopeFilter is custom-only, ignore cruises linked to an official itinerary
      if (scopeFilter === 'custom-only' && cruise.itineraryKey) return;

      const isCruiseOfficial = !!cruise.itineraryKey;

      if (cruise.departurePort) {
        addUsage(cruise.departurePort, isCruiseOfficial);
      }

      cruise.ports?.forEach(port => {
        if (port.name) {
          const isPortOfficial = isCruiseOfficial || port.fromItinerary === true;
          addUsage(port.name, isPortOfficial);
        }
      });
    });

    return stats;
  }, [cruises, scopeFilter]);

  // Extract all unique port names currently active under the selected scope
  const allPorts = useMemo(() => {
    return Object.keys(portUsageStats);
  }, [portUsageStats]);

  // Group similar ports
  useEffect(() => {
    if (allPorts.length === 0) {
      setPortGroups([]);
      return;
    }

    const groups: PortGroup[] = [];
    const processed = new Set<string>();

    allPorts.forEach(port => {
      if (processed.has(port)) return;

      const portDetails: Record<string, PortUsageStats> = {
        [port]: portUsageStats[port] || { officialCount: 0, customCount: 0, totalCount: 0 }
      };

      const groupPorts = [port];
      let usageCount = portUsageStats[port]?.totalCount || 0;
      let officialCruiseCount = portUsageStats[port]?.officialCount || 0;
      let customCruiseCount = portUsageStats[port]?.customCount || 0;

      // Find similar ports
      allPorts.forEach(otherPort => {
        if (port === otherPort || processed.has(otherPort)) return;

        const similarity = calculateSimilarity(port, otherPort);
        if (similarity >= similarityThreshold) {
          groupPorts.push(otherPort);
          const otherStats = portUsageStats[otherPort] || { officialCount: 0, customCount: 0, totalCount: 0 };
          portDetails[otherPort] = otherStats;
          usageCount += otherStats.totalCount;
          officialCruiseCount += otherStats.officialCount;
          customCruiseCount += otherStats.customCount;
          processed.add(otherPort);
        }
      });

      // Only add groups with multiple ports (potential duplicates)
      if (groupPorts.length > 1) {
        // Sort ports by total usage count (most used first) as default winner
        groupPorts.sort((a, b) => (portUsageStats[b]?.totalCount || 0) - (portUsageStats[a]?.totalCount || 0));
        
        groups.push({
          id: `group-${groups.length}`,
          ports: groupPorts,
          portDetails,
          selectedWinner: groupPorts[0],
          usageCount,
          officialCruiseCount,
          customCruiseCount,
          cleanupActivated: false // Default to off
        });
      }

      processed.add(port);
    });

    // Sort groups by usage count (most used first)
    groups.sort((a, b) => b.usageCount - a.usageCount);
    setPortGroups(groups);
  }, [allPorts, portUsageStats, similarityThreshold]);

  // Filter groups based on search
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return portGroups;
    
    const term = searchTerm.toLowerCase();
    return portGroups.filter(group =>
      group.ports.some(port => port.toLowerCase().includes(term))
    );
  }, [portGroups, searchTerm]);

  // Update winner for a group
  const updateWinner = (groupId: string, winner: string) => {
    setPortGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, selectedWinner: winner } : group
      )
    );
  };

  // Toggle cleanup activation for a group
  const toggleCleanupActivation = (groupId: string) => {
    setPortGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, cleanupActivated: !group.cleanupActivated } : group
      )
    );
  };

  // Initiate Cleanup (Builds mapping & checks for linked itinerary impacts)
  const handleApplyClick = () => {
    const activatedGroups = portGroups.filter(g => g.cleanupActivated);
    if (activatedGroups.length === 0) {
      toast.error('No activated groups to clean up. Please activate at least one group.');
      return;
    }

    // Build a mapping of old port names to new port names (only for activated groups)
    const portMapping: Record<string, string> = {};
    portGroups.forEach(group => {
      if (group.cleanupActivated) {
        group.ports.forEach(port => {
          if (port !== group.selectedWinner) {
            portMapping[port] = group.selectedWinner;
          }
        });
      }
    });

    if (Object.keys(portMapping).length === 0) {
      toast.error('No changes to apply');
      return;
    }

    // Calculate affected cruises
    let affectedUnlinkCount = 0;

    cruises.forEach(cruise => {
      if (scopeFilter === 'custom-only' && cruise.itineraryKey) return;

      let willBeUpdated = false;
      if (cruise.departurePort && portMapping[cruise.departurePort]) {
        willBeUpdated = true;
      }
      if (!willBeUpdated && cruise.ports?.some(p => p.name && portMapping[p.name])) {
        willBeUpdated = true;
      }

      if (willBeUpdated && cruise.itineraryKey) {
        affectedUnlinkCount++;
      }
    });

    if (affectedUnlinkCount > 0) {
      // Show confirmation modal for unlinking official itineraries
      setPendingMapping(portMapping);
      setPendingUnlinkCruisesCount(affectedUnlinkCount);
      setShowConfirmModal(true);
    } else {
      // Execute directly if no official itineraries will be unlinked
      executeCleanup(portMapping);
    }
  };

  // Perform actual execution of port cleanup across cruises
  const executeCleanup = async (portMapping: Record<string, string>) => {
    setIsProcessing(true);
    setShowConfirmModal(false);

    try {
      let updatedCount = 0;
      let unlinkedCount = 0;
      const updatePromises: Promise<void>[] = [];

      cruises.forEach(cruise => {
        // Skip linked cruises if filter is custom-only
        if (scopeFilter === 'custom-only' && cruise.itineraryKey) return;

        let needsUpdate = false;
        const cruiseUpdates: Partial<Cruise> = {};

        // Check departure port
        if (cruise.departurePort && portMapping[cruise.departurePort]) {
          cruiseUpdates.departurePort = portMapping[cruise.departurePort];
          needsUpdate = true;
        }

        // Check visited ports
        if (cruise.ports && cruise.ports.length > 0) {
          const updatedPorts = cruise.ports.map(port => {
            if (port.name && portMapping[port.name]) {
              needsUpdate = true;
              return {
                ...port,
                name: portMapping[port.name],
                fromItinerary: false // Customizing port name marks it as user-edited
              };
            }
            return port;
          });

          if (needsUpdate) {
            cruiseUpdates.ports = updatedPorts;
          }
        }

        if (needsUpdate) {
          // If cruise was linked to an official itinerary, break link to prevent automatic sync overwrite
          if (cruise.itineraryKey) {
            cruiseUpdates.itineraryKey = null;
            cruiseUpdates.linkBroken = true;
            cruiseUpdates.previousItineraryKey = cruise.itineraryKey;
            cruiseUpdates.unlinkedAt = Date.now();
            unlinkedCount++;
          }

          updatedCount++;
          updatePromises.push(updateCruise(cruise.id, cruiseUpdates, { silent: true }));
        }
      });

      await Promise.all(updatePromises);
      
      const unlinkedText = unlinkedCount > 0 ? ` (${unlinkedCount} official itinerary link${unlinkedCount > 1 ? 's' : ''} unlinked)` : '';
      toast.success(`Successfully updated ${updatedCount} cruise${updatedCount !== 1 ? 's' : ''}${unlinkedText}!`);
      
      // Refresh groups after update
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error applying cleanup:', error);
      toast.error('Failed to apply cleanup. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (cruisesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-secondary-500" />
      </div>
    );
  }

  return (
    <motion.div
      className="max-w-4xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Page Header */}
      <div className="mb-6 flex items-center">
        <button
          onClick={() => navigate('/profile')}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Port Name Cleanup</h1>
          <p className="text-gray-600 mt-1">Consolidate port name variations across your cruises</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 mb-1">How Port Cleanup Works</h3>
            <p className="text-sm text-blue-700">
              This tool groups similar port names so you can pick a canonical name. Port variations are badged by origin:
              <span className="font-semibold text-blue-900 ml-1">Official</span> (synced from cruise line itineraries) or
              <span className="font-semibold text-blue-900 ml-1">Custom</span> (manually created). Modifying official port names will break the sync link to prevent future automated updates from overwriting your custom cleanup.
            </p>
          </div>
        </div>
      </div>

      {/* Scope & Filtering Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 space-y-6">
        {/* Scope Selector Row */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
            <Filter className="h-4 w-4 mr-1.5 text-secondary-600" />
            Cleanup Scope Filter
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setScopeFilter('all')}
              className={`p-3.5 rounded-lg border-2 text-left transition-all ${
                scopeFilter === 'all'
                  ? 'border-secondary-500 bg-secondary-50/50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-900 text-sm">All Cruises</span>
                {scopeFilter === 'all' && <CheckCircle className="h-4 w-4 text-secondary-600" />}
              </div>
              <p className="text-xs text-gray-500">
                Include custom & official cruises. Modifying official ports will unlink sync status.
              </p>
            </button>

            <button
              type="button"
              onClick={() => setScopeFilter('custom-only')}
              className={`p-3.5 rounded-lg border-2 text-left transition-all ${
                scopeFilter === 'custom-only'
                  ? 'border-teal-500 bg-teal-50/50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-900 text-sm">Custom Cruises Only</span>
                {scopeFilter === 'custom-only' && <CheckCircle className="h-4 w-4 text-teal-600" />}
              </div>
              <p className="text-xs text-gray-500">
                Safe mode. Exclude linked official cruises so official syncs remain untouched.
              </p>
            </button>
          </div>
        </div>

        {/* Search & Threshold Row */}
        <div className="flex flex-col md:flex-row gap-4 md:items-end border-t border-gray-100 pt-5">
          <div className="flex-1 w-full">
            <label htmlFor="port-search-input" className="block text-sm font-medium text-gray-700 mb-2">
              Search Ports
            </label>
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="port-search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for port names..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                aria-label="Search Ports"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Similarity Threshold: {(similarityThreshold * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.3"
              max="1.0"
              step="0.05"
              value={similarityThreshold}
              onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className={`mt-2 rounded-md p-2 border ${
              similarityThreshold <= 0.5 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              {similarityThreshold <= 0.5 ? (
                <div className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-xs text-amber-700 font-medium">
                    Warning: Matches at 50% or below may be unreliable. Review groups carefully.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-600">
                  Lower = more groups (catch more variations), Higher = stricter matching
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Results Counter Bar */}
        <div className="pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-600">
            Found <strong>{filteredGroups.length}</strong> duplicate port group{filteredGroups.length !== 1 ? 's' : ''}
            {filteredGroups.length > 0 && (
              <span className="ml-2 text-gray-500">
                ({filteredGroups.reduce((sum, g) => sum + g.ports.length, 0)} total variations)
              </span>
            )}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Port Groups List */}
      {filteredGroups.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No duplicates found!</h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto">
            {searchTerm
              ? 'No port groups match your search. Try adjusting the search term or similarity threshold.'
              : scopeFilter === 'custom-only'
              ? 'No duplicates found among your custom cruises. Try switching scope to "All Cruises" if needed.'
              : 'All your port names appear to be unique. Great job keeping your data consistent!'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {filteredGroups.map((group) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Group {parseInt(group.id.replace('group-', '')) + 1}
                      </h3>
                      {group.officialCruiseCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                          <Link2Off className="w-3 h-3 mr-1 text-amber-600" />
                          Contains Official Syncs ({group.officialCruiseCount})
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Used in <strong>{group.usageCount}</strong> location{group.usageCount !== 1 ? 's' : ''}
                      {group.officialCruiseCount > 0 && group.customCruiseCount > 0 && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({group.officialCruiseCount} official, {group.customCruiseCount} custom)
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${group.cleanupActivated ? 'text-secondary-600' : 'text-gray-500'}`}>
                      {group.cleanupActivated ? 'Cleanup Activated' : 'Cleanup Inactive'}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleCleanupActivation(group.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 ${
                        group.cleanupActivated ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          group.cleanupActivated ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {group.ports.map((port) => {
                    const stats = group.portDetails[port] || { officialCount: 0, customCount: 0, totalCount: 0 };
                    const isSelected = group.selectedWinner === port;

                    return (
                      <label
                        key={port}
                        className={`flex items-center justify-between p-3.5 border-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-teal-500 bg-teal-50/70'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center flex-1 mr-4">
                          <input
                            type="radio"
                            name={group.id}
                            value={port}
                            checked={isSelected}
                            onChange={() => updateWinner(group.id, port)}
                            className="mr-3 text-secondary-600 focus:ring-secondary-500"
                          />
                          <div>
                            <div className="flex items-center flex-wrap gap-2">
                              <span className="font-medium text-gray-900">{port}</span>
                              <span className="text-xs text-gray-500 font-normal">
                                ({stats.totalCount} use{stats.totalCount !== 1 ? 's' : ''})
                              </span>
                            </div>

                            {/* Provenance Badges */}
                            <div className="flex items-center gap-1.5 mt-1">
                              {stats.officialCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-800">
                                  <RefreshCw className="w-3 h-3 mr-1 text-blue-600" />
                                  {stats.officialCount} Official
                                </span>
                              )}
                              {stats.customCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700">
                                  {stats.customCount} Custom
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {isSelected && (
                          <CheckCircle className="h-5 w-5 text-teal-500 flex-shrink-0" />
                        )}
                      </label>
                    );
                  })}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between flex-wrap text-sm">
                  <p className="text-gray-600">
                    <strong>Canonical Name:</strong> <span className="text-teal-700 font-semibold">{group.selectedWinner}</span>
                    {group.ports.filter(p => p !== group.selectedWinner).length > 0 && (
                      <span className="ml-2 text-amber-700 font-medium">
                        ({group.ports.filter(p => p !== group.selectedWinner).length} variation{group.ports.filter(p => p !== group.selectedWinner).length !== 1 ? 's' : ''} will be consolidated)
                      </span>
                    )}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Sticky Bottom Apply Action Bar */}
          <div className="bg-white rounded-lg shadow-md p-6 sticky bottom-4 z-10 border border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Ready to apply port cleanup?
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {filteredGroups.filter(g => g.cleanupActivated).length} group{filteredGroups.filter(g => g.cleanupActivated).length !== 1 ? 's' : ''} activated for cleanup.
                </p>
              </div>
              <button
                onClick={handleApplyClick}
                disabled={isProcessing || filteredGroups.filter(g => g.cleanupActivated).length === 0}
                className="flex items-center px-6 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Applying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Apply Cleanup ({filteredGroups.filter(g => g.cleanupActivated).reduce((sum, g) => sum + g.ports.filter(p => p !== g.selectedWinner).length, 0)} changes)
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Safety Unlink Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowConfirmModal(false)}
              />

              {/* Modal Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 z-10 border border-amber-200"
              >
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <ShieldAlert className="h-5 w-5 text-amber-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Official Itinerary Unlinking Warning
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  This cleanup action will update port names on <strong>{pendingUnlinkCruisesCount} cruise{pendingUnlinkCruisesCount !== 1 ? 's' : ''}</strong> currently linked to official cruise line itineraries.
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-2.5">
                    <Link2Off className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800 space-y-1">
                      <p className="font-semibold">Automatic Sync Disconnection</p>
                      <p>
                        Modifying official port names will break the itinerary sync for these cruises to prevent automated updates from overriding your changes. You can re-link them at any time from individual cruise detail pages.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => executeCleanup(pendingMapping)}
                    className="w-full py-3 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm transition-colors shadow-sm"
                  >
                    Confirm & Unlink {pendingUnlinkCruisesCount} Official Cruise{pendingUnlinkCruisesCount !== 1 ? 's' : ''}
                  </button>

                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="w-full py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>,
            document.body
          )
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PortCleanup;
